import * as React from 'react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { Loader2, UploadCloud, Plus, Sparkles } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useProductApi } from './api/product-api'
import { popModal } from '@/components/modals'
import type { Product } from './product-list'
import { ProjectMessage } from '@/lib/types'
import { nanoid } from 'nanoid'
import { useProjectPromptQueue } from '@/hooks/use-project-prompt-queue'
import { useAICoding } from '@/hooks/use-ai-coding'
import { useProjectStore } from '@/hooks/use-project-store'

const productSchema = z.object({
  name: z
    .string()
    .max(50, 'Product name must be 50 characters or less')
    .min(1, 'Product name is required'),
  description: z
    .string()
    .max(200, 'Description must be 200 characters or less')
    .optional(),
  price: z.number().min(0, 'Price must be positive'),
  currency: z.string().min(1, 'Currency is required'),
  status: z.enum(['active', 'inactive']),
  images: z.array(z.string()).optional(),
  billingType: z.enum(['one_time', 'recurring']),
  billingPeriod: z.enum(['day', 'week', 'month', 'year']).optional(),
  productConfig: z
    .record(z.union([z.string(), z.number(), z.array(z.string())]))
    .optional(),
})

type ProductFormData = z.infer<typeof productSchema>

interface ShoppingProductFormModalProps {
  product?: Product
  onSuccess?: () => void
  onClose?: () => void
}

export default function AddProductModal({
  product,
  onSuccess,
  onClose,
}: ShoppingProductFormModalProps) {
  const t = useTranslations('shopping-admin.products')
  const tUpload = useTranslations('shopping-admin.upload')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [fileUrlTab, setFileUrlTab] = useState<'upload' | 'url'>('upload')
  const [tempOnlineUrls, setTempOnlineUrls] = useState<
    Array<{ id: string; value: string; error?: string }>
  >([{ id: nanoid(), value: '' }])
  const [onlineUrls, setOnlineUrls] = useState<string[]>([]) // 存储多个在线地址
  const [selectedFiles, setSelectedFiles] = useState<
    Array<{ id: string; file: File }>
  >([])
  const [useAiEnhancement, setUseAiEnhancement] = useState(false)

  // URL校验函数
  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return true

    try {
      const urlObj = new URL(url)
      // 检查协议是否为 http 或 https
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }

  const {
    uploadFile,
    createProduct: apiCreateProduct,
    updateProduct: apiUpdateProduct,
  } = useProductApi()
  const { addToPromptQueue } = useProjectPromptQueue()
  const { aiCoding } = useAICoding()
  const { aiStage, addMessage, api__appendMessage } = useProjectStore()

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: '',
      description: '',
      price: 1000,
      currency: 'usd',
      status: 'active',
      images: [],
      billingType: 'one_time',
      billingPeriod: 'month',
      productConfig: {
        fileUrl: [], // 默认初始化 fileUrl 为数组，支持多文件
      },
    },
  })

  useEffect(() => {
    if (product) {
      const existingConfig = (product as any).productConfig || {}
      // 确保 fileUrl 始终是数组
      let fileUrl = existingConfig.fileUrl
      if (fileUrl && !Array.isArray(fileUrl)) {
        fileUrl = [fileUrl] // 兼容旧数据：将字符串转为数组
      } else if (!fileUrl) {
        fileUrl = []
      }

      form.reset({
        name: product.name,
        description: product.description,
        price: product.price / 100, // 转换为元
        currency: product.currency || 'usd',
        status: product.status,
        images: product.images || [],
        billingType: (product as any).billingType || 'one_time',
        billingPeriod: (product as any).billingPeriod || 'month',
        productConfig: {
          ...existingConfig,
          fileUrl,
        },
      })

      // 根据现有数据设置正确的tab和状态
      if (fileUrl && fileUrl.length > 0) {
        const firstFile = fileUrl[0]
        if (firstFile.startsWith('http')) {
          setFileUrlTab('url')
          // 将所有以 http 开头的 URL 加载到 onlineUrls
          const httpUrls = fileUrl.filter((url: string) =>
            url.startsWith('http'),
          )
          setOnlineUrls(httpUrls)
          // 初始化一个空的输入框
          setTempOnlineUrls([{ id: nanoid(), value: '' }])
        } else {
          setFileUrlTab('upload')
          setTempOnlineUrls([{ id: nanoid(), value: '' }])
          setOnlineUrls([])
        }
      }
    }
  }, [product, form])

  const handleClose = () => {
    onClose?.()
    popModal()
  }

  // 图片上传处理函数
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const currentImages = form.watch('images') || []
    const maxImages = 5
    const remainingSlots = maxImages - currentImages.length

    if (remainingSlots <= 0) {
      toast.error(tUpload('max_images'))
      return
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots)

    if (files.length > remainingSlots) {
      toast.warning(
        tUpload('remaining_slots', {
          remainingSlots,
          ignoredCount: files.length - remainingSlots,
        }),
      )
    }

    setUploading(true)
    try {
      const newImages = [...(form.watch('images') || [])]

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i]
        const formData = new FormData()
        formData.append('file', file)

        const { ossKey } = await uploadFile(formData)
        newImages.push(ossKey)
      }

      form.setValue('images', newImages)
    } catch (error) {
      console.error('Image upload error:', error)
      toast.error(tUpload('image_upload_failed'))
    } finally {
      setUploading(false)
    }
  }

  const removeImage = (index: number) => {
    const currentImages = form.watch('images') || []
    const newImages = currentImages.filter((_, i) => i !== index)
    form.setValue('images', newImages)
  }

  // 文件选择处理（选择但不立即上传）
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)

    // 累积添加文件到已选择的文件列表，为每个文件生成唯一ID
    const newFiles = fileArray.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${
        file.name
      }`,
      file,
    }))

    setSelectedFiles((prev) => {
      // 检查是否有重复的文件（基于文件名和大小）
      const existingFiles = prev.map(
        (item) => `${item.file.name}-${item.file.size}`,
      )
      const uniqueNewFiles = newFiles.filter(
        (item) =>
          !existingFiles.includes(`${item.file.name}-${item.file.size}`),
      )

      return [...prev, ...uniqueNewFiles]
    })

    // 清空input值，允许重复选择相同文件
    event.target.value = ''
  }

  // 上传所有选中的文件
  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) return

    setUploading(true)
    try {
      const currentConfig = form.watch('productConfig') || {}
      const currentFileUrls = (currentConfig.fileUrl as string[]) || []
      const newFileUrls = [...currentFileUrls]

      // 逐个上传文件
      for (const fileItem of selectedFiles) {
        const formData = new FormData()
        formData.append('file', fileItem.file)

        const { ossKey } = await uploadFile(formData)
        newFileUrls.push(ossKey)
      }

      // 更新 productConfig 中的 fileUrl 数组
      form.setValue('productConfig', {
        ...currentConfig,
        fileUrl: newFileUrls,
      })

      toast.success(
        selectedFiles.length === 1
          ? tUpload('file_upload_success')
          : tUpload('zip_upload_success', { fileCount: selectedFiles.length }),
      )

      // 清空已选文件
      setSelectedFiles([])
    } catch (error) {
      console.error('File upload error:', error)
      toast.error(tUpload('digital_file_upload_failed'))
    } finally {
      setUploading(false)
    }
  }

  // 移除单个已上传的文件
  const removeUploadedFile = (index: number) => {
    const currentConfig = form.watch('productConfig') || {}
    const currentFileUrls = (currentConfig.fileUrl as string[]) || []
    const newFileUrls = currentFileUrls.filter((_, i) => i !== index)

    form.setValue('productConfig', {
      ...currentConfig,
      fileUrl: newFileUrls,
    })
  }

  // 移除待上传的文件
  const removeSelectedFile = (id: string) => {
    setSelectedFiles((prev) => prev.filter((item) => item.id !== id))
  }

  // 添加在线地址（从输入框添加到已添加列表）
  const addOnlineUrl = (urlValue: string, inputId: string) => {
    if (!urlValue.trim()) {
      return
    }

    // 校验 URL
    if (!validateUrl(urlValue)) {
      // 更新对应输入框的错误状态
      setTempOnlineUrls((prev) =>
        prev.map((item) =>
          item.id === inputId
            ? { ...item, error: t('form.fields.invalidUrl') }
            : item,
        ),
      )
      return
    }

    // 检查是否重复
    if (onlineUrls.includes(urlValue)) {
      toast.error(t('form.fields.urlAlreadyAdded'))
      return
    }

    // 添加到已添加列表
    setOnlineUrls((prev) => [...prev, urlValue])

    // 清空对应的输入框
    setTempOnlineUrls((prev) =>
      prev.map((item) =>
        item.id === inputId ? { ...item, value: '', error: undefined } : item,
      ),
    )
  }

  // 添加新的输入框
  const addNewUrlInput = () => {
    setTempOnlineUrls((prev) => [...prev, { id: nanoid(), value: '' }])
  }

  // 删除输入框
  const removeUrlInput = (id: string) => {
    setTempOnlineUrls((prev) => {
      const item = prev.find((i) => i.id === id)
      // 如果输入框有值且有效，先添加到列表
      if (item && item.value.trim() && validateUrl(item.value)) {
        const urlValue = item.value
        // 检查是否重复
        if (!onlineUrls.includes(urlValue)) {
          setOnlineUrls((prevUrls) => [...prevUrls, urlValue])
        }
      }
      // 至少保留一个输入框
      if (prev.length <= 1) {
        return [{ id: nanoid(), value: '' }]
      }
      return prev.filter((i) => i.id !== id)
    })
  }

  // 删除在线地址
  const removeOnlineUrl = (index: number) => {
    setOnlineUrls((prev) => prev.filter((_, i) => i !== index))
  }

  const createProduct = async (data: any) => {
    if (useAiEnhancement) {
      // AI-enhanced flow
      const productName = data.name
      await executeAiCoding(
        buildAddProductPrompt(data),
        `${t('form.actions.createProductMessage')}: ${productName}`,
      )
    } else {
      // Direct API flow
      await apiCreateProduct(data)
    }
  }

  const updateProduct = async (productId: string, data: any) => {
    if (useAiEnhancement) {
      // AI-enhanced flow
      const productName = data.name
      await executeAiCoding(
        buildUpdateProductPrompt(productId, data),
        `${t('form.actions.updateProductMessage')}: ${productName}`,
      )
    } else {
      // Direct API flow
      await apiUpdateProduct(productId, data)
    }
  }

  const executeAiCoding = async (contentForAi: string, userContent: string) => {
    try {
      console.log('contentForAi', contentForAi)

      const userMessage: ProjectMessage = {
        id: nanoid(),
        role: 'user',
        content: userContent,
        contentForAi,
      }

      if (aiStage === 'coding') {
        addToPromptQueue(userMessage)
      } else {
        const newUserMessage = await api__appendMessage(userMessage)
        if (!newUserMessage) {
          toast.error('Failed to save product message')
          return
        }
        addMessage(newUserMessage)
        aiCoding({ addedUserMessage: newUserMessage })
      }
    } catch (error) {
      console.error('Error saving product via AI:', error)
      toast.error('Failed to save product via AI')
    }
  }

  const onSubmit = async (data: ProductFormData) => {
    setLoading(true)
    try {
      // 处理 productConfig，特别是 fileUrl 字段
      const productConfig = { ...(data.productConfig || {}) }
      console.log('productConfig', productConfig)
      console.log('fileUrlTab', fileUrlTab)
      console.log('onlineUrls', onlineUrls)
      // 如果 fileUrl 存在且是对象的一部分，根据当前 tab 决定使用哪个值
      if (productConfig.hasOwnProperty('fileUrl')) {
        if (fileUrlTab === 'url') {
          // URL 方式，使用 onlineUrls 数组
          if (onlineUrls.length > 0) {
            productConfig.fileUrl = onlineUrls
          } else {
            // 如果没有在线地址，删除该字段
            delete productConfig.fileUrl
          }
        } else if (fileUrlTab === 'upload') {
          // 如果是上传文件方式，处理文件URL数组
          const fileUrls = productConfig.fileUrl as string[]
          if (fileUrls && Array.isArray(fileUrls) && fileUrls.length > 0) {
            // 为每个不是 http 开头的 URL 拼接前缀
            productConfig.fileUrl = fileUrls.map((url) => {
              if (!url.startsWith('http')) {
                return `https://pub-fcdf2a6833174e68902966cf14dc111d.r2.dev/${url}`
              }
              return url
            })
          } else {
            // 如果没有文件，删除该字段
            delete productConfig.fileUrl
          }
        }

        // 如果 fileUrl 为空数组，删除该字段
        if (
          Array.isArray(productConfig.fileUrl) &&
          productConfig.fileUrl.length === 0
        ) {
          delete productConfig.fileUrl
        }
      }

      const submitData: any = {
        name: data.name,
        description: data.description,
        price: data.price * 100,
        currency: data.currency,
        status: data.status,
        images: data.images,
        billingType: data.billingType,
        billingPeriod:
          data.billingType === 'recurring' ? data.billingPeriod : undefined,
        productConfig,
      }

      if (product) {
        await updateProduct(product.id, submitData)
      } else {
        await createProduct(submitData)
      }

      toast.success(
        product ? t('form.update.success') : t('form.create.success'),
      )
      onSuccess?.()
      handleClose()
    } catch (error) {
      console.error('Product save error:', error)
      toast.error(product ? t('form.update.error') : t('form.create.error'))
      setLoading(false)
    }
  }

  const isRecurring = form.watch('billingType') === 'recurring'

  // 检查 productConfig 是否为空对象
  const productConfig = form.watch('productConfig') || {}
  const hasProductConfig = Object.keys(productConfig).length > 0

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        id={product ? 'update-product-modal' : 'add-product-modal'}
        className="max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden p-0 rounded-[20px] border-gray-700 flex flex-col mx-2 sm:mx-4"
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-4 sm:px-8 pt-4 sm:pt-6 pb-4">
          <DialogTitle className="text-lg sm:text-xl font-semibold text-white">
            {product ? t('form.edit.title') : t('form.create.title')}
          </DialogTitle>
          {product && (
            <p className="text-sm text-white/60 mt-1">ID: {product.id}</p>
          )}
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="flex-1 overflow-y-auto space-y-4 sm:space-y-6 px-4 sm:px-8 pb-4 sm:pb-6 max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-120px)]">
            {/* 第一步：商品基本信息 */}
            <div className="space-y-3">
              <Label htmlFor="name" className="text-sm font-medium text-white">
                {t('form.fields.name')}
              </Label>
              <Input
                id="name"
                {...form.register('name')}
                placeholder={t('form.fields.namePlaceholder')}
                className="h-11 text-sm bg-white/10 border-gray-600 text-white placeholder:text-gray-400 focus:border-white/30"
                outerClassName="!relative !flex !size-full !items-center"
              />
              {form.formState.errors.name && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="description"
                  className="text-sm font-medium text-white"
                >
                  {t('form.fields.description')}
                </Label>
                <span className="text-xs text-white/60">
                  {form.watch('description')?.length || 0}/100
                </span>
              </div>
              <Textarea
                id="description"
                {...form.register('description')}
                placeholder={t('form.fields.descriptionPlaceholder')}
                rows={3}
                maxLength={100}
                className="text-sm resize-none bg-white/10 border-gray-600 text-white placeholder:text-gray-400 focus:border-white/30"
              />
              {form.formState.errors.description && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>

            {/* 图片上传区域 */}
            <div className="space-y-4">
              <Label
                htmlFor="images"
                className="text-sm font-medium text-white"
              >
                {t('form.fields.images')}
              </Label>
              <div className="space-y-4">
                {/* 上传按钮和已上传图片在同一行 */}
                <div className="flex items-center gap-1">
                  {/* 上传按钮 */}
                  <div>
                    <Label
                      htmlFor="images-upload"
                      className={`flex flex-col items-center justify-center w-20 h-20 rounded-xl cursor-pointer transition-all flex-shrink-0 ${
                        (form.watch('images') || []).length >= 5
                          ? 'border border-white/40 bg-white/5 text-gray-400 cursor-not-allowed'
                          : 'border border-white/40 hover:border-white/60 hover:bg-white/10 text-white'
                      }`}
                      style={{
                        background: '#FFFFFF0A',
                        border: '1px dashed #FFFFFF66',
                        borderStyle: 'dashed',
                        borderImage: 'none',
                      }}
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin mb-1" />
                      ) : (
                        <UploadCloud className="h-4 w-4 mb-1" />
                      )}
                    </Label>
                    <Input
                      id="images-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={(form.watch('images') || []).length >= 5}
                    />
                  </div>
                  {/* 已上传的图片预览 - 紧挨着上传按钮右边，从左往右依次排列 */}
                  {(form.watch('images') || []).map((image, index) => (
                    <div key={index} className="relative group flex-shrink-0">
                      <div className="h-20 w-20 rounded-xl overflow-hidden border border-white/40 cursor-pointer hover:opacity-80 transition-opacity shadow-sm">
                        <img
                          src={`${
                            image.startsWith('http')
                              ? image
                              : `https://pub-fcdf2a6833174e68902966cf14dc111d.r2.dev/${image}`
                          }`}
                          alt={`Product ${index + 1}`}
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-1 -right-1 bg-white text-black rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-400">
                  {t('form.fields.imagesUploaded', {
                    count: (form.watch('images') || []).length,
                    max: 5,
                  })}
                </div>
              </div>
              {form.formState.errors.images && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.images.message}
                </p>
              )}
            </div>

            {/* 第二步：价格设置 */}
            <div className="space-y-3">
              <Label htmlFor="price" className="text-sm font-medium text-white">
                {t('form.fields.price')}
              </Label>
              {/* 收费方式 Tab 切换 */}
              <div className="flex space-x-1 bg-white/5 p-1 rounded-lg mb-3">
                <button
                  type="button"
                  onClick={() => form.setValue('billingType', 'one_time')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    form.watch('billingType') === 'one_time'
                      ? 'bg-white/20 text-white shadow-sm'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  {t('form.fields.billingTypes.one_time')}
                </button>
                <button
                  type="button"
                  onClick={() => form.setValue('billingType', 'recurring')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    form.watch('billingType') === 'recurring'
                      ? 'bg-white/20 text-white shadow-sm'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  {t('form.fields.billingTypes.recurring')}
                </button>
              </div>

              <div className="flex gap-2 h-11 items-center">
                <Select
                  value={form.watch('currency')}
                  onValueChange={(value) => form.setValue('currency', value)}
                >
                  <SelectTrigger className="w-28 h-11 text-sm bg-white/10 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem
                      value="usd"
                      className="text-white hover:bg-gray-700"
                    >
                      USD
                    </SelectItem>
                    <SelectItem
                      value="eur"
                      className="text-white hover:bg-gray-700"
                    >
                      EUR
                    </SelectItem>
                    <SelectItem
                      value="gbp"
                      className="text-white hover:bg-gray-700"
                    >
                      GBP
                    </SelectItem>
                    <SelectItem
                      value="cny"
                      className="text-white hover:bg-gray-700"
                    >
                      CNY
                    </SelectItem>
                    <SelectItem
                      value="rub"
                      className="text-white hover:bg-gray-700"
                    >
                      RUB
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  {...form.register('price', { valueAsNumber: true })}
                  placeholder="0.00"
                  className="h-11 text-sm flex-1 bg-white/10 border-gray-600 text-white placeholder:text-gray-400 focus:border-white/30"
                  outerClassName="!relative !flex !items-center"
                />
                {isRecurring && (
                  <>
                    <span className="text-sm text-gray-400">/</span>
                    <Select
                      value={form.watch('billingPeriod')}
                      onValueChange={(value) =>
                        form.setValue(
                          'billingPeriod',
                          value as 'day' | 'week' | 'month' | 'year',
                        )
                      }
                    >
                      <SelectTrigger className="w-32 h-11 text-sm bg-white/10 border-gray-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600">
                        <SelectItem
                          value="day"
                          className="text-white hover:bg-gray-700"
                        >
                          {t('form.fields.billingPeriods.day')}
                        </SelectItem>
                        <SelectItem
                          value="week"
                          className="text-white hover:bg-gray-700"
                        >
                          {t('form.fields.billingPeriods.week')}
                        </SelectItem>
                        <SelectItem
                          value="month"
                          className="text-white hover:bg-gray-700"
                        >
                          {t('form.fields.billingPeriods.month')}
                        </SelectItem>
                        <SelectItem
                          value="year"
                          className="text-white hover:bg-gray-700"
                        >
                          {t('form.fields.billingPeriods.year')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
              {form.formState.errors.billingType && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.billingType.message}
                </p>
              )}
              {form.formState.errors.billingPeriod && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.billingPeriod.message}
                </p>
              )}
              {form.formState.errors.price && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.price.message}
                </p>
              )}
              {form.formState.errors.currency && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.currency.message}
                </p>
              )}
            </div>

            {/* 第三步：商品状态 */}
            <div className="space-y-3">
              <Label
                htmlFor="status"
                className="text-sm font-medium text-white"
              >
                {t('form.fields.status')}
              </Label>
              <Select
                value={form.watch('status')}
                onValueChange={(value) =>
                  form.setValue('status', value as 'active' | 'inactive')
                }
              >
                <SelectTrigger className="w-full h-11 text-sm bg-white/10 border-gray-600 text-white">
                  <SelectValue placeholder={t('form.fields.selectStatus')} />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem
                    value="active"
                    className="text-white hover:bg-gray-700"
                  >
                    {t('status.active')}
                  </SelectItem>
                  <SelectItem
                    value="inactive"
                    className="text-white hover:bg-gray-700"
                  >
                    {t('status.inactive')}
                  </SelectItem>
                </SelectContent>
              </Select>
              {form.formState.errors.status && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.status.message}
                </p>
              )}
            </div>

            {/* 商品配置 */}
            {hasProductConfig && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-white">
                  {t('form.fields.productConfig')}
                </Label>
                <div className="space-y-3">
                  {Object.entries(productConfig).map(([key, value]) => {
                    // 特殊处理 fileUrl 字段
                    if (key === 'fileUrl') {
                      return (
                        <div
                          key={key}
                          className="space-y-4 border-2 border-gray-700 rounded-xl p-4 bg-white/5"
                        >
                          <Label className="text-sm font-medium text-white">
                            {t('form.fields.fileUrlLabel')}
                          </Label>

                          {/* Tab切换 */}
                          <div className="flex space-x-1 bg-white/10 p-1 rounded-lg">
                            <button
                              type="button"
                              onClick={() => {
                                setFileUrlTab('upload')
                                setOnlineUrls([])
                                setTempOnlineUrls([{ id: nanoid(), value: '' }])
                              }}
                              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                                fileUrlTab === 'upload'
                                  ? 'bg-white/20 text-white shadow-sm'
                                  : 'text-gray-400 hover:text-gray-300'
                              }`}
                            >
                              {t('form.fields.uploadFile')}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setFileUrlTab('url')
                                setSelectedFiles([])
                                // 确保至少有一个输入框
                                if (tempOnlineUrls.length === 0) {
                                  setTempOnlineUrls([
                                    { id: nanoid(), value: '' },
                                  ])
                                }
                              }}
                              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                                fileUrlTab === 'url'
                                  ? 'bg-white/20 text-white shadow-sm'
                                  : 'text-gray-400 hover:text-gray-300'
                              }`}
                            >
                              {t('form.fields.onlineUrl')}
                            </button>
                          </div>

                          {/* Tab内容 */}
                          <div className="space-y-4">
                            {/* 文件上传Tab */}
                            {fileUrlTab === 'upload' && (
                              <div className="space-y-4">
                                {/* 已上传的文件列表 */}
                                {Array.isArray(value) && value.length > 0 && (
                                  <div className="space-y-2">
                                    <div className="text-sm text-gray-400">
                                      {t('form.fields.uploadedFiles', {
                                        count: value.length,
                                      })}
                                    </div>
                                    <div className="space-y-2">
                                      {value.map(
                                        (fileUrl: string, index: number) => (
                                          <div
                                            key={index}
                                            className="flex items-center gap-3 p-3 border border-gray-600 rounded-lg bg-white/10"
                                          >
                                            <div className="text-sm flex-1">
                                              <div className="text-white break-all">
                                                {fileUrl.split('/').pop()}
                                              </div>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                removeUploadedFile(index)
                                              }
                                              className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50/10 transition-colors flex-shrink-0"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* 待上传的文件列表 */}
                                {selectedFiles.length > 0 && (
                                  <div className="space-y-2">
                                    <div className="text-sm text-blue-400">
                                      {t('form.fields.pendingFiles', {
                                        count: selectedFiles.length,
                                      })}
                                    </div>
                                    <div className="space-y-2">
                                      {selectedFiles.map((fileItem) => (
                                        <div
                                          key={fileItem.id}
                                          className="flex items-center gap-3 p-3 border border-blue-500/50 rounded-lg bg-blue-500/10"
                                        >
                                          <div className="text-sm flex-1">
                                            <div className="text-white">
                                              {fileItem.file.name}
                                            </div>
                                            <div className="text-gray-400 text-xs mt-1">
                                              {(
                                                fileItem.file.size / 1024
                                              ).toFixed(1)}{' '}
                                              KB
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              removeSelectedFile(fileItem.id)
                                            }
                                            className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50/10 transition-colors flex-shrink-0"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                    <Button
                                      type="button"
                                      onClick={handleUploadFiles}
                                      disabled={uploading}
                                      className="w-full"
                                      loading={uploading}
                                    >
                                      {uploading
                                        ? t('form.fields.uploading')
                                        : t('form.fields.uploadFiles', {
                                            count: selectedFiles.length,
                                          })}
                                    </Button>
                                  </div>
                                )}

                                {/* 文件选择按钮 */}
                                <Label
                                  htmlFor={`file-upload-${key}`}
                                  className="flex items-center justify-center w-full h-12 px-4 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-gray-500 hover:bg-white/5 transition-all"
                                >
                                  <UploadCloud className="h-4 w-4 mr-2" />
                                  <span className="text-sm font-medium text-white">
                                    {t('form.fields.selectDigitalFile')}
                                  </span>
                                </Label>
                                <Input
                                  id={`file-upload-${key}`}
                                  type="file"
                                  accept="*"
                                  multiple
                                  onChange={handleFileSelect}
                                  className="hidden"
                                  disabled={uploading}
                                />
                              </div>
                            )}

                            {/* 在线地址Tab */}
                            {fileUrlTab === 'url' && (
                              <div className="space-y-4">
                                {/* 已添加的在线地址列表 */}
                                {onlineUrls.length > 0 && (
                                  <div className="space-y-2">
                                    <div className="text-sm text-gray-400">
                                      {t('form.fields.addedUrls', {
                                        count: onlineUrls.length,
                                      })}
                                    </div>
                                    <div className="space-y-2">
                                      {onlineUrls.map((url, index) => (
                                        <div
                                          key={index}
                                          className="flex items-center gap-3 p-3 border border-gray-600 rounded-lg bg-white/10"
                                        >
                                          <div className="text-sm flex-1">
                                            <div className="text-white break-all">
                                              {url}
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              removeOnlineUrl(index)
                                            }
                                            className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50/10 transition-colors flex-shrink-0"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* 添加新地址 */}
                                <div className="space-y-3">
                                  <Label className="text-sm font-medium text-white/80">
                                    {t('form.fields.digitalProductUrl')}
                                  </Label>
                                  <div className="space-y-2">
                                    {tempOnlineUrls.map((urlInput, index) => (
                                      <div
                                        key={urlInput.id}
                                        className="flex gap-2"
                                      >
                                        <Input
                                          id={`url-${key}-${urlInput.id}`}
                                          value={urlInput.value}
                                          onChange={(e) => {
                                            const urlValue = e.target.value
                                            setTempOnlineUrls((prev) =>
                                              prev.map((item) =>
                                                item.id === urlInput.id
                                                  ? {
                                                      ...item,
                                                      value: urlValue,
                                                      error:
                                                        urlValue.trim() &&
                                                        !validateUrl(urlValue)
                                                          ? t(
                                                              'form.fields.invalidUrl',
                                                            )
                                                          : undefined,
                                                    }
                                                  : item,
                                              ),
                                            )
                                          }}
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault()
                                              addOnlineUrl(
                                                urlInput.value,
                                                urlInput.id,
                                              )
                                            }
                                          }}
                                          onBlur={(e) => {
                                            // 失去焦点时自动添加有效的 URL
                                            const urlValue = e.target.value
                                            if (
                                              urlValue.trim() &&
                                              validateUrl(urlValue)
                                            ) {
                                              addOnlineUrl(
                                                urlValue,
                                                urlInput.id,
                                              )
                                            }
                                          }}
                                          placeholder={t(
                                            'form.fields.digitalProductUrlPlaceholder',
                                          )}
                                          className={`h-11 text-sm bg-white/10 border-gray-600 text-white placeholder:text-gray-400 focus:border-white/30 ${
                                            urlInput.error
                                              ? 'border-red-500 focus:border-red-500'
                                              : ''
                                          }`}
                                          outerClassName="!relative !flex !flex-1 !items-center"
                                        />
                                        {tempOnlineUrls.length > 1 && (
                                          <Button
                                            type="button"
                                            onClick={() =>
                                              removeUrlInput(urlInput.id)
                                            }
                                            className="h-11 w-11 p-0 bg-red-500/20 hover:bg-red-500/30 text-red-400 flex items-center justify-center"
                                          >
                                            ×
                                          </Button>
                                        )}
                                        {index ===
                                          tempOnlineUrls.length - 1 && (
                                          <Button
                                            type="button"
                                            onClick={addNewUrlInput}
                                            className="h-11 w-11 p-0 bg-white/20 hover:bg-white/30 text-white flex items-center justify-center"
                                          >
                                            <Plus className="h-5 w-5" />
                                          </Button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  {tempOnlineUrls.some(
                                    (item) => item.error,
                                  ) && (
                                    <div className="text-sm text-red-500 mt-1">
                                      {
                                        tempOnlineUrls.find(
                                          (item) => item.error,
                                        )?.error
                                      }
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-400">
                                    {t(
                                      'form.fields.digitalProductUrlDescription',
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    }

                    // 其他字段的常规处理
                    const isNumber = typeof value === 'number'
                    return (
                      <div key={key} className="space-y-2">
                        <Label
                          htmlFor={`config-${key}`}
                          className="text-sm font-medium text-white/80"
                        >
                          {key}
                        </Label>
                        <Input
                          id={`config-${key}`}
                          type={isNumber ? 'number' : 'text'}
                          value={value as string | number}
                          onChange={(e) => {
                            const newConfig = { ...productConfig }
                            newConfig[key] = isNumber
                              ? Number(e.target.value)
                              : e.target.value
                            form.setValue('productConfig', newConfig)
                          }}
                          placeholder={`Enter ${key}`}
                          className="h-11 text-sm bg-white/10 border-gray-600 text-white placeholder:text-gray-400 focus:border-white/30"
                          outerClassName="!relative !flex !size-full !items-center"
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 sm:pt-6 flex-shrink-0 px-4 sm:px-8 pb-4 sm:pb-6 space-y-4">
            {/* AI Enhancement Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-gray-700">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-400" />
                <div>
                  <Label
                    htmlFor="ai-enhancement"
                    className="text-sm font-medium text-white cursor-pointer"
                  >
                    {t('form.fields.aiEnhancement')}
                  </Label>
                  <p className="text-xs text-gray-400">
                    {t('form.fields.aiEnhancementDescription')}
                  </p>
                </div>
              </div>
              <Switch
                id="ai-enhancement"
                checked={useAiEnhancement}
                onCheckedChange={setUseAiEnhancement}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="w-full sm:flex-1 h-11 text-sm font-medium bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                disabled={loading || uploading}
              >
                {t('form.actions.cancel')}
              </Button>
              <Button
                type="submit"
                className="w-full sm:flex-1 h-11 text-sm font-medium bg-white text-black hover:bg-gray-100"
                loading={loading}
                disabled={uploading}
              >
                {product ? t('form.actions.update') : t('form.actions.create')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function buildAddProductPrompt(data: any) {
  const {
    name = '',
    description = '',
    price = 0,
    currency = 'usd',
    status = 'active',
    images = [],
    billingType = 'one_time',
    billingPeriod = 'month',
    productConfig = {},
  } = data

  const getBillingTypeInfo = () => {
    if (billingType === 'one_time') {
      return '- Billing Type: One-time payment'
    } else if (billingType === 'recurring') {
      return `- Billing Type: Recurring payment\n- Billing Period: ${billingPeriod}`
    }
    return '- Billing type not provided'
  }

  const getImagesInfo = () => {
    if (!images || images.length === 0) {
      return '- No images'
    }
    return images
      .map(
        (imageUrl: string, index: number) =>
          `- Image ${index + 1}: ${imageUrl}`,
      )
      .join('\n')
  }

  const getProductConfigInfo = () => {
    if (!productConfig || Object.keys(productConfig).length === 0) {
      return '- No configuration'
    }
    return Object.entries(productConfig)
      .map(([key, value]) => {
        if (key === 'fileUrl' && Array.isArray(value)) {
          if (value.length === 0) {
            return `- ${key}: No files`
          }
          const fileList = value
            .map((url: string, idx: number) => `  - File ${idx + 1}: ${url}`)
            .join('\n')
          return `- ${key}:\n${fileList}`
        }
        if (Array.isArray(value)) {
          return `- ${key}: [${value.join(', ')}]`
        }
        return `- ${key}: ${value}`
      })
      .join('\n')
  }

  return `
You are a professional e-commerce product creation assistant. Please help the user refine and optimize this product based on the following information:

## Basic Product Information
- **Product Name**: ${name}
- **Description**: ${description}
- **Currency**: ${currency}
- **Price (in cents)**: ${price}
- **Status**: ${status === 'active' ? 'Active' : 'Inactive'}

## Billing Information
${getBillingTypeInfo()}

## Product Images
${getImagesInfo()}

## Product Configuration
${getProductConfigInfo()}
  `
}

function buildUpdateProductPrompt(productId: string, data: any) {
  const {
    name = '',
    description = '',
    price = 0,
    currency = 'usd',
    status = 'active',
    images = [],
    billingType = 'one_time',
    billingPeriod = 'month',
    productConfig = {},
  } = data

  const getBillingTypeInfo = () => {
    if (billingType === 'one_time') {
      return '- Billing Type: One-time payment'
    } else if (billingType === 'recurring') {
      return `- Billing Type: Recurring payment\n- Billing Period: ${billingPeriod}`
    }
    return '- Billing type not provided'
  }

  const getImagesInfo = () => {
    if (!images || images.length === 0) {
      return '- No images'
    }
    return images
      .map(
        (imageUrl: string, index: number) =>
          `- Image ${index + 1}: ${imageUrl}`,
      )
      .join('\n')
  }

  const getProductConfigInfo = () => {
    if (!productConfig || Object.keys(productConfig).length === 0) {
      return '- No configuration'
    }
    return Object.entries(productConfig)
      .map(([key, value]) => {
        if (key === 'fileUrl' && Array.isArray(value)) {
          if (value.length === 0) {
            return `- ${key}: No files`
          }
          const fileList = value
            .map((url: string, idx: number) => `  - File ${idx + 1}: ${url}`)
            .join('\n')
          return `- ${key}:\n${fileList}`
        }
        if (Array.isArray(value)) {
          return `- ${key}: [${value.join(', ')}]`
        }
        return `- ${key}: ${value}`
      })
      .join('\n')
  }

  return `
You are a professional e-commerce product management assistant. Please help the user update this product based on the following information:

## Product ID
${productId}

## Updated Product Information
- **Product Name**: ${name}
- **Description**: ${description}
- **Currency**: ${currency}
- **Price (in cents)**: ${price}
- **Status**: ${status === 'active' ? 'Active' : 'Inactive'}

## Billing Information
${getBillingTypeInfo()}

## Product Images
${getImagesInfo()}

## Product Configuration
${getProductConfigInfo()}
  `
}
